<template>
  <tabs>
    <template #actions>
      <Switch
        v-model="hideDeleted"
        :label="$t('hideDeletedDrivers')"
      />
    </template>
  </tabs>
  <toolbar class="toolbar">
    <filter-pane v-model="filter" :filters="filters" />
  </toolbar>

  <busy-area :busy="loading">
    <main class="px-12 mb-10 whitespace-pre">
      <div class="card-grid">
        <card v-for="stats of statisticCards" :key="stats.title">
          <div class="card-content">
            <span v-if="stats.type === 'icon'" class="icon flex-1">
              <component :is="stats.icon" />
            </span>
            <div v-else class="flex-1 flex items-center">
              <Progress
                :value="stats.progress ?? 50"
                color="var(--color-primary)"
                background-color="rgba(var(--color-primary-rgb), 0.5)"
              />
            </div>
            <span class="block text-pre-title-big mt-auto">{{ stats.title }}</span>
            <span class="data">{{ stats.data }}</span>
          </div>
        </card>
      </div>
    </main>
  </busy-area>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Card from '@utils/Card.vue';
import BusyArea from '@utils/busy/BusyArea.vue';
import BicycleIcon from '@images/bicycle.svg?component';
import EmissionIcon from '@images/emissions.svg?component';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import ClockIcon from '@images/clock.svg?component';
import { useI18n } from 'vue-i18n';
import Progress from '@utils/Progress.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import {
  collOptionsProvider,
  type Filter,
  type ProviderParams,
  useProvider,
} from '@/components/utils/table/hooks/provider';
import type { Campaign, Region, Statistics } from '@/entities/Entities';
import Switch from '@utils/inputs/Switch.vue';

const { t, n } = useI18n();

const filter = ref<Filter>({});

const hideDeleted = ref(true);

const totalFilter = computed(() => ({ hideDeleted: hideDeleted.value, ...filter.value }));

const filters = [
  {
    key: 'date',
    type: 'datetime',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      label: t('statistics.overview.date'),
    },
  },
  {
    key: 'region',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      options: collOptionsProvider<Region>('/regions', {
        sort: {
          sortKey: 'name',
          sortDirection: 'ASC',
        },
      }, (value) => ({
        label: value.name,
        value: value.id,
      })),
      label: t('statistics.overview.region'),
    },
  },
  {
    key: 'campaignGroup',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      searchable: true,
      minChars: 2,
      filterResults: false,
      clearOnSelect: true,
      delay: 500,
      createOption: false,
      resolveOnLoad: true,
      noOptionsText: t('inputSearchString'),
      options: (query: string, select: any) => {
        const input = query || select.modelValue;
        if (!input) return Promise.resolve([]);
        const filterQuery: ProviderParams = {
          filter: query ? { name: input } : { id: select.modelValue },
        };
        return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
          label: value.name,
          value: value.id,
        }))();
      },
      label: t('statistics.overview.campaignGroup'),
    },
  },
];

const providerParams = reactive({
  filter: totalFilter,
});

const { data: statistics, loading } = useProvider<Statistics>('/statistics', providerParams);

const statisticCards = computed(() => [
  {
    type: 'icon',
    title: t('pages.dashboard.overview.totalDistance'),
    data: statistics.value?.totalDistance
      ? `${n(Math.round(statistics.value.totalDistance / 1000), '', 'de-CH')} km`
      : '0 Km',
    icon: BicycleIcon,
  },
  {
    type: 'icon',
    title: t('pages.dashboard.overview.totalCo2'),
    data: statistics.value?.totalCo2 ? `${n(statistics.value.totalCo2, '', 'de-CH')} kg` : '0 Kg',
    icon: EmissionIcon,
  },
  {
    type: 'icon',
    title: t('pages.dashboard.overview.totalDrivingTime'),
    data: statistics.value?.totalDrivingTime
      ? `${n(Math.round(statistics.value.totalDrivingTime / 3600), '', 'de-CH')} ${t('unit.hours')}`
      : `0 ${t('unit.hours')}`,
    icon: ClockIcon,
  },
  {
    type: 'progress',
    title: t('pages.dashboard.overview.totalCompleteDriverProfiles'),
    progress: statistics.value
      ? (statistics.value.totalCompleteDriverProfiles / statistics.value.totalAmountOfDrivers) * 100
      : 50,
    data: statistics.value
      ? `${n(statistics.value.totalCompleteDriverProfiles, '', 'de-CH')} / ${n(
        statistics.value.totalAmountOfDrivers,
        '',
        'de-CH',
      )}`
      : '0',
  },
  {
    type: 'progress',
    title: t('pages.dashboard.overview.totalActiveBoxes'),
    progress: statistics.value
      ? (statistics.value.totalActiveBoxes / statistics.value.totalAmountOfDrivers) * 100
      : 50,
    data: statistics.value
      ? `${n(statistics.value.totalActiveBoxes, '', 'de-CH')} / ${n(statistics.value.totalAmountOfDrivers, '', 'de-CH')}`
      : '0',
  },
]);
</script>

<style lang="scss" scoped>
.icon {
  @apply flex items-center text-dark;
  @apply mb-12;
  @apply h-15;
  max-width: px(70);
}

.card-grid {
  @apply grid gap-x-4 gap-y-15;
  grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
}

.data {
  @apply h1;
}

.card {
  @apply flex flex-col;
}

.card-content {
  @apply flex flex-col gap-y-2.5 h-full;
  min-height: px(200);
}
</style>
